import React, { useState, useEffect } from "react";
import { Modal } from "antd";
import AddNewOrder from "./AddOrderComponents/AddNewOrder";
import SelectPackageStep from "./AddOrderComponents/SelectPackage";
import AdditionalDetailsStep from "./AddOrderComponents/AdditionalDetails";
import ReviewConfirmStep from "./AddOrderComponents/ReviewOrder";
import OrderSuccessModal from "./AddOrderComponents/OrderSuccess";
import { useDispatch, useSelector } from "react-redux";
import { actions } from "../slice";
import * as selectors from "../selectors";
// import CodeList from "./AddOrderComponents/CodeList";

export default function AddOrderWizard({
  visible,
  currentStep,
  userRole,
  onNext,
  onBack,
  onClose,
  commodities = [],
  states = [],
  districts = [],
  samplers = [],
  sectors = [],
  projectCodes = [],
  companies = [],
  packages = [],
  fetchingPackages = false,
  packagesError = null,
  onSubmit,
  onFieldChange,
  setWizardData,
  wizardData = {
    basicInfo: {},
    // codeListData: [],
    selectedPackage: null,
    additionalInfo: {},
  },
}) {
  const dispatch = useDispatch();
  const [successVisible, setSuccessVisible] = useState(false);
  const [createdOrderId, setCreatedOrderId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [projectConfigLoading, setProjectConfigLoading] = useState(false);

  const projectData = useSelector(selectors.selectProjectData);
  const agencies = useSelector(selectors.selectAgencies);

  // Step 0 → Step 1: Save basic info and trigger package fetch
  const handleStep1Next = (data) => {
    onNext(data);
  };

  // Step 1 → Step 2: Save code list data and trigger package fetch
  // const handleStep2Next = (codeListEntries) => {
  //   onNext(codeListEntries);
  // };

  // Step 1 → Step 2: Save selected package
  const handleStep2Next = (packageData) => {
    const transformedPackage = {
      id: packageData?._id,
      name: packageData?.name,
      totalParameters: packageData?.totalParametersForTest,
      default: packageData?.isDefaultPackage,
      categories: Object.entries(packageData?.testCategoryCounts || {}).map(
        ([name, data]) => ({
          name,
          count: data?.count,
          parameters: data?.parameters,
        })
      ),
    };
    onNext(transformedPackage);
  };

  // Step 2 → Step 1: Save selected package
  const handleStep2Back = (packageData) => {
    const transformedPackage = {
      id: packageData?._id,
      name: packageData?.name,
      totalParameters: packageData?.totalParametersForTest,
      default: packageData?.isDefaultPackage,
      categories: Object.entries(packageData?.testCategoryCounts || {}).map(
        ([name, data]) => ({
          name,
          count: data?.count,
          parameters: data?.parameters,
        })
      ),
    };
    onBack(transformedPackage);
  };

  // Step 3 → Step 4: Save additional details
  const handleStep3Next = (data) => {
    onNext(data);
  };

  // Step 3 → Step 2: Save additional details
  const handleStep3Back = (data) => {
    onBack(data);
  };

  // Step 3 → Submit: Create order
  const handleCreateOrder = () => {
    setIsSubmitting(true);
    onSubmit({
      onSuccess: (orderId) => {
        setIsSubmitting(false);
        setCreatedOrderId(orderId);
        setSuccessVisible(true);
      },
      onFailure: () => {
        setIsSubmitting(false);
        onClose();
      },
    });
  };

  const getDisplayBasicInfo = () => {
    const info = wizardData.basicInfo;

    return {
      commodity: commodities.find((c) => c._id === info.commodity)?.name,
      variant: info.variant,
      sampleType: info.sampleType,
      sector: sectors.find((s) => s.value === info.sector)?.label,
      state: states.find((s) => s._id === info.state)?.name,
      district: districts.find((d) => d._id === info.district)?.city,
      client: companies.find((c) => c._id === info.client)?.companyName,
      projectCode: projectCodes.find((p) => p._id === info.projectCode)
        ?.projectCode,
      sampler: samplers.find((s) => s._id === info.sampler)?.name,
      siteAddress: info.siteAddress,
      destinationCountry: info.destinationCountry,
      samplingType: info.samplingType,
      codewiseReason: info.codewiseReason,
      expectedConsignmentReadiness: info.expectedConsignmentReadiness,
      expectedShipmentDate: info.expectedShipmentDate,
      agency: agencies.find((a) => a._id === info.agency)?.agencyName,
    };
  };

  useEffect(() => {
    if (!visible) {
      setSuccessVisible(false);
      setCreatedOrderId(null);
    }
  }, [visible]);

  const projectCodesId = wizardData.basicInfo.projectCode;
  useEffect(() => {
    setProjectConfigLoading(true);
    dispatch(
      actions.fetchProjectConfig({
        projectCodeId: projectCodesId,
        onSuccess: () => setProjectConfigLoading(false),
        onFailure: () => setProjectConfigLoading(false),
      })
    );

    // Clear dependent fields when project code changes
    setWizardData((prevData) => ({
      ...prevData,
      basicInfo: {
        client: prevData.basicInfo.client,
        projectCode: prevData.basicInfo.projectCode,
        agency: "",
        commodity: "",
        variant: "",
        sampleType: "",
        sector: "",
        state: "",
        district: "",
        destinationCountry: "",
        samplingType: "",
        codewiseReason: "",
        expectedConsignmentReadiness: null,
        expectedShipmentDate: null,
        sampler: "",
        siteAddress: "",
      },
      selectedPackage: null,
      additionalInfo: {
        batchId: "",
        supplier: "",
        quantity: "",
        quantityUnit: "",
        notes: "",
        documents: [],
      },
    }));
  }, [dispatch, projectCodesId]);

  console.log({ wizardData });
  return (
    <>
      <Modal
        visible={visible && !successVisible}
        footer={null}
        width={950}
        closable={true}
        onCancel={onClose}
        bodyStyle={{ maxHeight: "78vh", overflowY: "auto", padding: 25 }}
        title="Add New Order"
        destroyOnClose
        maskClosable={false}
      >
        {currentStep === 0 && (
          <AddNewOrder
            onNext={handleStep1Next}
            onCancel={onClose}
            initialData={wizardData.basicInfo}
            commodities={commodities}
            states={states}
            districts={districts}
            samplers={samplers}
            sectors={sectors}
            projectCodes={projectCodes}
            companies={companies}
            onFieldChange={onFieldChange}
            userRole={userRole}
            projectData={projectData}
            projectConfigLoading={projectConfigLoading}
            agencies={agencies}
          />
        )}
        {/* {currentStep === 1 && (
          <CodeList 
            onBack={onBack}
            onNext={handleStep2Next}
            onCancel={onClose}
            initialData={wizardData.codeListData}
          />
        )} */}

        {currentStep === 1 && (
          <SelectPackageStep
            onNext={handleStep2Next}
            onBack={handleStep2Back}
            selectedPackageId={wizardData.selectedPackage?.id}
            packages={packages}
            loading={fetchingPackages}
            error={packagesError}
          />
        )}

        {currentStep === 2 && (
          <AdditionalDetailsStep
            onNext={handleStep3Next}
            onBack={handleStep3Back}
            onCancel={onClose}
            initialData={wizardData.additionalInfo}
            projectData={projectData}
            projectConfigLoading={projectConfigLoading}
          />
        )}

        {currentStep === 3 && (
          <ReviewConfirmStep
            basicInfo={getDisplayBasicInfo()}
            selectedPackage={wizardData.selectedPackage}
            additionalInfo={wizardData.additionalInfo}
            onBack={onBack}
            onCancel={onClose}
            onSubmit={handleCreateOrder}
            loading={isSubmitting}
            projectData={projectData}
            userRole={userRole}
          />
        )}
      </Modal>

      <OrderSuccessModal
        visible={successVisible}
        orderId={createdOrderId}
        onClose={onClose}
      />
    </>
  );
}
